import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { MAXAuthBootstrap } from "@/components/auth/MAXAuthBootstrap";
import { EngagementTracker } from "@/components/EngagementTracker";
import { UtmAndAnalyticsBootstrap } from "@/components/UtmAndAnalyticsBootstrap";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/AppLayout";
import { DashboardSkeleton, TableSkeleton } from "@/components/dashboard/DashboardSkeleton";
import { PricingSkeleton } from "@/components/PricingSkeleton";
import { RootRoute } from "@/components/RootRoute";
import Landing from "./pages/Landing";
const Auth = lazy(() => import("./pages/Auth"));

// Lazy-load heavy authenticated pages for sub-1s LCP
const Index = lazy(() => import("./pages/Index"));
const Inbox = lazy(() => import("./pages/Inbox"));
const Contracts = lazy(() => import("./pages/Contracts"));
const Tokens = lazy(() => import("./pages/Tokens"));
const Mark = lazy(() => import("./pages/Mark"));
const Reports = lazy(() => import("./pages/Reports"));
const Settings = lazy(() => import("./pages/Settings"));
const Pricing = lazy(() => import("./pages/Pricing"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const ResourceHub = lazy(() => import("./pages/ResourceHub"));
const Blog = lazy(() => import("./pages/Blog"));
const PublicArticle = lazy(() => import("./pages/PublicArticle"));
const Articles = lazy(() => import("./pages/Articles"));
const TaxMonitoring = lazy(() => import("./pages/TaxMonitoring"));
const Export = lazy(() => import("./pages/Export"));
const AdminMessages = lazy(() => import("./pages/AdminMessages"));
const AdminFeedback = lazy(() => import("./pages/AdminFeedback"));
const AdminInvoices = lazy(() => import("./pages/admin/AdminInvoices"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Offer = lazy(() => import("./pages/Offer"));
const Privacy = lazy(() => import("./pages/Privacy"));
const DataPolicy = lazy(() => import("./pages/DataPolicy"));
const Documents = lazy(() => import("./pages/Documents"));
const Refund = lazy(() => import("./pages/Refund"));
const DemoDashboard = lazy(() => import("./pages/DemoDashboard"));
const PenaltyGuide = lazy(() => import("./pages/PenaltyGuide"));
const VKCallback = lazy(() => import("./pages/VKCallback"));
const KnowledgeBase = lazy(() => import("./pages/KnowledgeBase"));
const Feedback = lazy(() => import("./pages/Feedback"));
const About = lazy(() => import("./pages/About"));
const Contacts = lazy(() => import("./pages/Contacts"));
const Zakony = lazy(() => import("./pages/Zakony"));
const Garantii = lazy(() => import("./pages/Garantii"));
const Cases = lazy(() => import("./pages/Cases"));
const Vs = lazy(() => import("./pages/Vs"));
const Demo = lazy(() => import("./pages/Demo"));
const CalculatorPage = lazy(() => import("./pages/CalculatorPage"));
const Cheklisty = lazy(() => import("./pages/Cheklisty"));
const ForBuhgalter = lazy(() => import("./pages/ForBuhgalter"));
const ForYurist = lazy(() => import("./pages/ForYurist"));
const ForAgency = lazy(() => import("./pages/ForAgency"));
const ForBlogger = lazy(() => import("./pages/ForBlogger"));
const Slovar = lazy(() => import("./pages/Slovar"));
const ChangelogZakona = lazy(() => import("./pages/ChangelogZakona"));
const SelfPromo = lazy(() => import("./pages/SelfPromo"));
const SeoLanding = lazy(() => import("./pages/SeoLanding"));
const Partners = lazy(() => import("./pages/Partners"));
const Api = lazy(() => import("./pages/Api"));
const Docs = lazy(() => import("./pages/Docs"));
const ApiKeys = lazy(() => import("./pages/ApiKeys"));
const EridonNew = lazy(() => import("./pages/EridonNew"));

// Enterprise OS (private /app/*)
const EnterpriseAppLayout = lazy(() =>
  import("./components/enterprise/app/EnterpriseAppLayout").then((m) => ({ default: m.EnterpriseAppLayout }))
);
const AppDashboard = lazy(() => import("./pages/app/AppDashboard"));
const AppScanner = lazy(() => import("./pages/app/AppScanner"));
const AppCompliance = lazy(() => import("./pages/app/AppCompliance"));
const AppAnalytics = lazy(() => import("./pages/app/AppAnalytics"));
const AppErid = lazy(() => import("./pages/app/AppErid"));
const AppIntegrations = lazy(() => import("./pages/app/AppIntegrations"));
const AppTeams = lazy(() => import("./pages/app/AppTeams"));
const AppApiPage = lazy(() => import("./pages/app/AppApi"));
const AppBilling = lazy(() => import("./pages/app/AppBilling"));
const AppSettings = lazy(() => import("./pages/app/AppSettings"));
const AppWhiteLabel = lazy(() => import("./pages/app/AppWhiteLabel"));

const queryClient = new QueryClient();

const PageFallback = () => <DashboardSkeleton />;

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <SubscriptionProvider>
            <AppErrorBoundary>
              <MAXAuthBootstrap />
              <UtmAndAnalyticsBootstrap />
              <EngagementTracker />
              <Routes>
              <Route path="/auth" element={<Suspense fallback={<PageFallback />}><Auth /></Suspense>} />
              {/* Алиасы — редиректы на канонические пути, чтобы не плодить дубликаты для SEO */}
              <Route path="/auth/login" element={<Navigate to="/auth?mode=login" replace />} />
              <Route path="/auth/register" element={<Navigate to="/auth?mode=signup" replace />} />
              <Route path="/auth/signup" element={<Navigate to="/auth?mode=signup" replace />} />
              <Route path="/auth/forgot" element={<Navigate to="/auth?mode=forgot" replace />} />
              {/* Канонический путь дашборда */}
              <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
              <Route path="/tariffs" element={<Navigate to="/pricing" replace />} />
              <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
              <Route path="/terms-of-service" element={<Navigate to="/offer" replace />} />
              <Route path="/auth/vk-callback" element={<Suspense fallback={<PageFallback />}><VKCallback /></Suspense>} />
              <Route path="/pricing" element={<Suspense fallback={<PricingSkeleton />}><Pricing /></Suspense>} />
              <Route path="/landing" element={<Navigate to="/" replace />} />
              <Route path="/eridon-new" element={<Suspense fallback={<PageFallback />}><EridonNew /></Suspense>} />
              <Route path="/reset-password" element={<Suspense fallback={<PageFallback />}><ResetPassword /></Suspense>} />
              <Route path="/payment-success" element={<Suspense fallback={<PageFallback />}><PaymentSuccess /></Suspense>} />
              <Route path="/article/:slug" element={<Suspense fallback={<PageFallback />}><PublicArticle /></Suspense>} />
              <Route path="/articles" element={<Suspense fallback={<PageFallback />}><Articles /></Suspense>} />
              
              <Route path="/offer" element={<Suspense fallback={<PageFallback />}><Offer /></Suspense>} />
              <Route path="/privacy" element={<Suspense fallback={<PageFallback />}><Privacy /></Suspense>} />
              <Route path="/data-policy" element={<Suspense fallback={<PageFallback />}><DataPolicy /></Suspense>} />
              <Route path="/terms" element={<Navigate to="/offer" replace />} />
              <Route path="/documents" element={<Suspense fallback={<PageFallback />}><Documents /></Suspense>} />
              <Route path="/refund" element={<Suspense fallback={<PageFallback />}><Refund /></Suspense>} />
              <Route path="/demo-dashboard" element={<Suspense fallback={<PageFallback />}><DemoDashboard /></Suspense>} />
              <Route path="/penalty-guide" element={<Suspense fallback={<PageFallback />}><PenaltyGuide /></Suspense>} />
              <Route path="/knowledge-base" element={<Suspense fallback={<PageFallback />}><KnowledgeBase /></Suspense>} />
              <Route path="/feedback" element={<Suspense fallback={<PageFallback />}><Feedback /></Suspense>} />
              <Route path="/about" element={<Suspense fallback={<PageFallback />}><About /></Suspense>} />
              <Route path="/contacts" element={<Suspense fallback={<PageFallback />}><Contacts /></Suspense>} />
              <Route path="/contact" element={<Navigate to="/contacts" replace />} />
              <Route path="/zakony" element={<Suspense fallback={<PageFallback />}><Zakony /></Suspense>} />
              <Route path="/garantii" element={<Suspense fallback={<PageFallback />}><Garantii /></Suspense>} />
              <Route path="/cases" element={<Suspense fallback={<PageFallback />}><Cases /></Suspense>} />
              <Route path="/vs" element={<Suspense fallback={<PageFallback />}><Vs /></Suspense>} />
              <Route path="/demo" element={<Suspense fallback={<PageFallback />}><Demo /></Suspense>} />
              <Route path="/calculator" element={<Suspense fallback={<PageFallback />}><CalculatorPage /></Suspense>} />
              <Route path="/cheklisty" element={<Suspense fallback={<PageFallback />}><Cheklisty /></Suspense>} />
              <Route path="/buhgalteru" element={<Suspense fallback={<PageFallback />}><ForBuhgalter /></Suspense>} />
              <Route path="/yuristu" element={<Suspense fallback={<PageFallback />}><ForYurist /></Suspense>} />
              <Route path="/agentstvu" element={<Suspense fallback={<PageFallback />}><ForAgency /></Suspense>} />
              <Route path="/blogeru" element={<Suspense fallback={<PageFallback />}><ForBlogger /></Suspense>} />
              <Route path="/slovar" element={<Suspense fallback={<PageFallback />}><Slovar /></Suspense>} />
              <Route path="/changelog-zakona" element={<Suspense fallback={<PageFallback />}><ChangelogZakona /></Suspense>} />
              <Route path="/self-promo" element={<Suspense fallback={<PageFallback />}><SelfPromo /></Suspense>} />

              {/* SEO landings (sprint 2) */}
              <Route path="/markirovka-reklamy" element={<Suspense fallback={<PageFallback />}><SeoLanding slug="markirovka-reklamy" /></Suspense>} />
              <Route path="/markirovka-telegram" element={<Suspense fallback={<PageFallback />}><SeoLanding slug="markirovka-telegram" /></Suspense>} />
              <Route path="/markirovka-vk" element={<Suspense fallback={<PageFallback />}><SeoLanding slug="markirovka-vk" /></Suspense>} />
              <Route path="/erid" element={<Suspense fallback={<PageFallback />}><SeoLanding slug="erid" /></Suspense>} />
              <Route path="/erir" element={<Suspense fallback={<PageFallback />}><SeoLanding slug="erir" /></Suspense>} />
              <Route path="/ord" element={<Suspense fallback={<PageFallback />}><SeoLanding slug="ord" /></Suspense>} />
              <Route path="/shtrafy-za-reklamu" element={<Suspense fallback={<PageFallback />}><SeoLanding slug="shtrafy-za-reklamu" /></Suspense>} />
              <Route path="/markirovka-dlya-ip" element={<Suspense fallback={<PageFallback />}><SeoLanding slug="markirovka-dlya-ip" /></Suspense>} />
              <Route path="/markirovka-dlya-samozanyatyh" element={<Suspense fallback={<PageFallback />}><SeoLanding slug="markirovka-dlya-samozanyatyh" /></Suspense>} />
              <Route path="/markirovka-blogerov" element={<Suspense fallback={<PageFallback />}><SeoLanding slug="markirovka-blogerov" /></Suspense>} />

              {/* SEO comparisons (sprint 3) */}
              <Route path="/eridon-vs-elama" element={<Suspense fallback={<PageFallback />}><SeoLanding slug="eridon-vs-elama" /></Suspense>} />
              <Route path="/eridon-vs-vk-ord" element={<Suspense fallback={<PageFallback />}><SeoLanding slug="eridon-vs-vk-ord" /></Suspense>} />
              <Route path="/eridon-vs-yandex-ord" element={<Suspense fallback={<PageFallback />}><SeoLanding slug="eridon-vs-yandex-ord" /></Suspense>} />

              {/* Sprint 5 — trust pages */}
              <Route path="/partners" element={<Suspense fallback={<PageFallback />}><Partners /></Suspense>} />
              <Route path="/api" element={<Suspense fallback={<PageFallback />}><Api /></Suspense>} />
              <Route path="/docs" element={<Suspense fallback={<PageFallback />}><Docs /></Suspense>} />
              {/* Legacy redirect */}
              <Route path="/enterprise/dashboard" element={<Navigate to="/app/dashboard" replace />} />

              {/* Enterprise OS — fully isolated /app/* */}
              <Route
                path="/app"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <EnterpriseAppLayout />
                  </Suspense>
                }
              >
                <Route index element={<Navigate to="/app/dashboard" replace />} />
                <Route path="dashboard" element={<AppDashboard />} />
                <Route path="scanner" element={<AppScanner />} />
                <Route path="compliance" element={<AppCompliance />} />
                <Route path="analytics" element={<AppAnalytics />} />
                <Route path="erid" element={<AppErid />} />
                <Route path="integrations" element={<AppIntegrations />} />
                <Route path="teams" element={<AppTeams />} />
                <Route path="api" element={<AppApiPage />} />
                <Route path="billing" element={<AppBilling />} />
                <Route path="admin/invoices" element={<ProtectedRoute requiredRole="admin"><AdminInvoices /></ProtectedRoute>} />
                <Route path="settings" element={<AppSettings />} />
                <Route path="white-label" element={<AppWhiteLabel />} />
                <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
              </Route>

              <Route
                path="/"
                element={
                  <RootRoute
                    landing={<Landing />}
                    dashboard={
                      <AppLayout>
                        <Suspense fallback={<PageFallback />}>
                          <Index />
                        </Suspense>
                      </AppLayout>
                    }
                  />
                }
              />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Suspense fallback={<PageFallback />}>
                        <Routes>
                          <Route path="/inbox" element={<Inbox />} />
                          <Route path="/contracts" element={<Contracts />} />
                          <Route path="/tokens" element={<Tokens />} />
                          <Route path="/mark" element={<Mark />} />
                          <Route path="/reports" element={<Reports />} />
                          <Route path="/resources" element={<ResourceHub />} />
                          <Route path="/resources/:slug" element={<ResourceHub />} />
                          <Route path="/blog" element={<Blog />} />
                          <Route path="/blog/:slug" element={<Blog />} />
                          <Route path="/tax-monitoring" element={<TaxMonitoring />} />
                          <Route path="/export" element={<Export />} />
                          <Route path="/admin/messages" element={<ProtectedRoute requiredRole="admin"><AdminMessages /></ProtectedRoute>} />
                          <Route path="/admin/feedback" element={<ProtectedRoute requiredRole="admin"><AdminFeedback /></ProtectedRoute>} />
                          <Route path="/admin/invoices" element={<ProtectedRoute requiredRole="admin"><AdminInvoices /></ProtectedRoute>} />
                          <Route path="/settings" element={<Settings />} />
                          <Route path="/api-keys" element={<ApiKeys />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              </Routes>
            </AppErrorBoundary>
          </SubscriptionProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
